import React from 'react';
import { useTheme, useThemeMode } from '../../theme/ThemeContext';
import { usePriceSubscription } from '../../hooks/usePriceSubscription';
import { UserIcon, BriefcaseIcon, StarIcon, ActivityIcon, SunIcon, MoonIcon, SettingsIcon, LogOutIcon } from '../../helpers/Icons';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsDark, setInitialized, setIsLoggedIn, setUser } from '../../redux/features/platformSlice';
import { useNavigate } from 'react-router-dom';
import { clearAuthStorage } from '../../services/platform/api';

const Logo = () => {
  const darkMode = useSelector(selectIsDark)
  const logoUrl = darkMode ? '/assets/img/logo-dark.png' : '/assets/img/logo-white.png';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
      {/* <div style={{ width: 28, height: 28, borderRadius: 8, background: `linear-gradient(135deg,${theme.primary},#6366f1)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 0 14px ${theme.primary}55` }}> */}
        <img src={logoUrl} alt="Core Prime LTD" style={{ width: 100, height: 32 }} />
      {/* </div> */}
      {/* <div className="topbar-user-name">
        <div style={{ fontSize: 12, fontWeight: 800, color: theme.text, letterSpacing: '-0.3px' }}>
          Core Prime LTD
        </div>
        <div style={{ fontSize: 8, fontWeight: 600, color: theme.textM, letterSpacing: '0.05em', marginTop: -1 }}>TRADE</div>
      </div> */}
    </div>
  );
};

const SymbolTicker = ({ symbol, item }) => {
  const theme = useTheme();
  const price = usePriceSubscription(symbol);
  if (!price || !symbol) return null;
  const pct = price.bid && price.open && price.open !== 0 ? ((price.bid - price.open) / price.open * 100) : 0;
  const pos = pct >= 0;
  const d = item?.digits ?? price.digits ?? 5;
  return (
    <div className="topbar-symbol-ticker" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 10px', background: theme.card2, borderRadius: 7, border: `1px solid ${theme.border}`, flexShrink: 0 }}>
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: theme.text }}>{symbol}</div>
        <div style={{ fontSize: 8, color: theme.textM }}>{item?.description ?? ''}</div>
      </div>
      <div style={{ fontFamily: theme.mono, fontVariantNumeric: 'tabular-nums' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>{price.bid.toFixed(d)}</div>
      </div>
      <span style={{ fontSize: 9, fontWeight: 700, color: pos ? theme.pos : theme.neg }}>{pos ? '+' : ''}{pct.toFixed(2)}%</span>
    </div>
  );
};

const NAV = [
  { id: 'watchlist', label: 'Watchlist', Icon: StarIcon },
  // { id: 'markets', label: 'Markets', Icon: GlobeIcon },
  { id: 'trade', label: 'Trade', Icon: ActivityIcon },
  { id: 'portfolio', label: 'Portfolio', Icon: BriefcaseIcon },
];

const Topbar = ({ activeNav, setActiveNav, activeSymbol, activeItem }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useThemeMode();
  const account = useSelector(s => s.platform.account);
  const user = useSelector(s => s.platform.user);
  const [showProfileMenu, setShowProfileMenu] = React.useState(false);

  const menuRef = React.useRef();
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowProfileMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    clearAuthStorage('logout');
    dispatch(setUser(null));
    dispatch(setIsLoggedIn(false));
    dispatch(setInitialized(false));
    navigate('/login', { replace: true });
  };
  return (
    <div style={{ background: theme.surface, borderBottom: `1px solid ${theme.border}`, padding: '0 10px', display: 'flex', alignItems: 'center', gap: 8, height: 48, flexShrink: 0, position: 'sticky', top: 0, zIndex: 50 }}>
      <Logo />
      <div style={{ width: 1, height: 24, background: theme.border, flexShrink: 0 }} />
      {activeSymbol && <SymbolTicker symbol={activeSymbol} item={activeItem} />}
      <div style={{ flex: 1 }} />

      {/* Nav */}
      <nav style={{ display: 'flex', gap: 1 }}>
        {NAV.map(({ id, label, Icon }) => (
          <button key={id} onClick={() => setActiveNav(id)} style={{
            display: 'flex', alignItems: 'center', gap: 4, padding: '5px 8px', borderRadius: 6, border: 'none', cursor: 'pointer',
            fontFamily: theme.sans, fontSize: 10, fontWeight: 600,
            background: activeNav === id ? theme.primaryGlow : 'transparent',
            color: activeNav === id ? theme.primaryL : theme.textS,
            borderBottom: `2px solid ${activeNav === id ? theme.primary : 'transparent'}`,
          }}>
            <Icon s={12} c="currentColor" />
            <span className="nav-label">{label}</span>
          </button>
        ))}
      </nav>
      <div style={{ width: 1, height: 24, background: theme.border, flexShrink: 0 }} />

      <button onClick={toggleTheme} style={{ width: 28, height: 28, borderRadius: 6, background: 'transparent', border: `1px solid ${theme.border}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {isDark ? <SunIcon s={13} c={theme.orange} /> : <MoonIcon s={13} c={theme.textS} />}
      </button>

      <div style={{ position: 'relative' }} ref={menuRef}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 8px', background: theme.card2, borderRadius: 7, border: `1px solid ${theme.border}`, flexShrink: 0, cursor: 'pointer' }}
          onClick={() => setShowProfileMenu(!showProfileMenu)}
        >
          <div style={{ width: 22, height: 22, borderRadius: '50%', background: `linear-gradient(135deg,${theme.primary},#6366f1)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <UserIcon s={11} c="#fff" />
          </div>
          <div className="topbar-user-name">
            <div style={{ fontSize: 9, fontWeight: 600, color: theme.text }}>{user?.name || 'Demo'}</div>
            <div style={{ fontSize: 7, color: theme.textM, fontFamily: theme.mono }}>{account.login}</div>
          </div>
        </div>

        {showProfileMenu && (
          <div style={{
            position: 'absolute', top: 35, right: 0, width: 220, background: theme.card,
            border: `1px solid ${theme.border}`, borderRadius: 10, padding: '10px 0',
            boxShadow: '0 15px 40px rgba(0,0,0,0.5)', zIndex: 2000
          }}>
            <div style={{ padding: '10px 16px', borderBottom: `1px solid ${theme.border}44`, marginBottom: 5 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: theme.text }}>{user?.name || 'Demo User'}</div>
              <div style={{ fontSize: 10, color: theme.textM, marginTop: 2 }}>{user?.email || 'demo@example.com'}</div>
              <div style={{ fontSize: 9, color: theme.primaryL, fontFamily: theme.mono, marginTop: 4 }}>Account: {account.login}</div>
            </div>

            <button onClick={() => { setActiveNav('profile'); setShowProfileMenu(false); }} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
              background: 'transparent', border: 'none', color: theme.textS, fontSize: 11, fontWeight: 600, cursor: 'pointer',
              textAlign: 'left', transition: 'background 0.2s'
            }} onMouseEnter={e => e.target.style.background = theme.card2} onMouseLeave={e => e.target.style.background = 'transparent'}>
              <UserIcon s={14} c={theme.textM} /> Profile
            </button>

            <button onClick={() => { setActiveNav('settings'); setShowProfileMenu(false); }} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
              background: 'transparent', border: 'none', color: theme.textS, fontSize: 11, fontWeight: 600, cursor: 'pointer',
              textAlign: 'left', transition: 'background 0.2s'
            }} onMouseEnter={e => e.target.style.background = theme.card2} onMouseLeave={e => e.target.style.background = 'transparent'}>
              <SettingsIcon s={14} c={theme.textM} /> Settings
            </button>

            <div style={{ height: 1, background: `${theme.border}44`, margin: '5px 0' }} />

            <button onClick={handleLogout} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
              background: 'transparent', border: 'none', color: theme.neg, fontSize: 11, fontWeight: 700, cursor: 'pointer',
              textAlign: 'left', transition: 'background 0.2s'
            }} onMouseEnter={e => e.target.style.background = theme.card2} onMouseLeave={e => e.target.style.background = 'transparent'}>
              <LogOutIcon s={14} c={theme.neg} /> Logout
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
export default Topbar;
